import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { Sphere } from 'types/global';
import type { SphereOwnGlyph } from 'types/glyph';
import { globalSphereCache, type CachedSphereData } from './GlobalSphereCache';
import { patchSphereProperties } from './patchSphereProperties';
import { glyphKindOf } from './glyph/sphereGlyph';

/**
 * Upload a new avatar image for a sphere via the canonical slot path.
 * Invalidates the client-side GlobalSphereCache entry so the next render
 * triggers a fresh batch-info fetch.
 *
 * Always use this wrapper — never call the slot PUT endpoint directly.
 * See /standards/frontend/sphere-avatar.md.
 */
export async function uploadSphereAvatar(
  graphClient: ApiClient,
  sphereId: string,
  file: File,
): Promise<void> {
  const formData = new FormData();
  formData.append('file', file);
  await graphClient.put(`/spheres/${sphereId}/slots/avatar`, formData);
  globalSphereCache.clearSphere(sphereId);
}

/**
 * Delete the avatar slot for a sphere.
 * Invalidates the client-side GlobalSphereCache entry on every outcome, so the
 * next render triggers a fresh batch-info fetch and shows what the server holds.
 * A failed removal may still have changed the server: the slot DELETE removes
 * the legacy `imageId` before the slot row, so a 500 can follow a removed
 * picture, and after a 5xx or a timeout the server state is unknown.
 *
 * A 404 `SLOT_EMPTY` means the sphere already has no picture (removed
 * elsewhere): the removal's outcome holds, so it resolves, and the next read
 * shows the sphere without one. Any other failure, including a 404 for a
 * sphere that no longer exists, is rethrown after the cache is cleared.
 *
 * Always use this wrapper — never call the slot DELETE endpoint directly.
 * See /standards/frontend/sphere-avatar.md.
 */
export async function deleteSphereAvatar(
  graphClient: ApiClient,
  sphereId: string,
): Promise<void> {
  try {
    await graphClient.delete(`/spheres/${sphereId}/slots/avatar`);
  } catch (err) {
    if (!isSlotAlreadyEmpty(err)) throw err;
  } finally {
    globalSphereCache.clearSphere(sphereId);
  }
}

/** The slot DELETE's 404 code when neither the slot nor the legacy `imageId` held a picture. */
const SLOT_EMPTY = 'SLOT_EMPTY';

function isSlotAlreadyEmpty(err: unknown): boolean {
  const e = (err ?? {}) as { status?: number; response?: { status?: number; data?: { error?: unknown } } };
  return (e.response?.status ?? e.status) === 404 && e.response?.data?.error === SLOT_EMPTY;
}

/**
 * Sets a sphere's own glyph and/or colour (the `center_glyph` aspect). Only the
 * keys present in `patch` are written, so a colour change never freezes the
 * type's glyph; an explicit `null` clears that one field back to the type's.
 * `glyph_kind` is derived from the glyph's shape, never taken from the caller,
 * and is cleared with the glyph so no stale kind outlives it.
 * Invalidates the client-side GlobalSphereCache entry after success.
 *
 * Always use this wrapper — never PATCH the glyph properties directly.
 * See /standards/frontend/sphere-avatar.md.
 */
export async function setSphereGlyph(
  graphClient: ApiClient,
  sphereId: string,
  patch: SphereOwnGlyph,
): Promise<Sphere> {
  const properties: Record<string, unknown> = {};
  if ('glyph' in patch) {
    properties['glyph'] = patch.glyph ?? null;
    properties['glyph_kind'] = patch.glyph ? glyphKindOf(patch.glyph) : null;
  }
  if ('glyphColor' in patch) {
    properties['glyph_color'] = patch.glyphColor ?? null;
  }
  const sphere = await patchSphereProperties(graphClient, { sphereId, properties });
  globalSphereCache.clearSphere(sphereId);
  return sphere;
}

/**
 * Clears all of a sphere's own glyph values so it inherits its type's glyph and
 * colour again. Clears rather than copying the type's values, which would freeze
 * the sphere against later changes to the type.
 */
export async function resetSphereGlyph(graphClient: ApiClient, sphereId: string): Promise<Sphere> {
  const sphere = await patchSphereProperties(graphClient, {
    sphereId,
    properties: { glyph: null, glyph_kind: null, glyph_color: null },
  });
  globalSphereCache.clearSphere(sphereId);
  return sphere;
}

/**
 * Re-reads a sphere's info and picture from the server, past the cached entry. For a picture that
 * was uploaded but is still being scanned: the read after the upload caches the sphere without
 * it, so only a read that skips the cache can see it once the scan clears.
 *
 * Always use this wrapper — never clear the cache entry directly to force a re-read.
 * See /standards/frontend/sphere-avatar.md.
 */
export async function rereadSphereAvatar(graphClient: ApiClient, sphereId: string): Promise<CachedSphereData> {
  globalSphereCache.clearSphere(sphereId);
  return globalSphereCache.getSphereData(sphereId, graphClient);
}
